import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';

interface DLCInputProps {
  /** ISO date string (YYYY-MM-DD) or empty */
  value: string;
  onChange: (isoDate: string) => void;
  /** Compact mode for inline usage (e.g., stock list) */
  compact?: boolean;
  /** Additional className */
  className?: string;
  /** Show color coding based on expiry status */
  colorCoded?: boolean;
}

/**
 * DLC (Date Limite de Consommation) input component.
 * - Large, touch-friendly (min 48px height in normal mode)
 * - Keyboard entry in DD/MM/YYYY format
 * - Native date picker via calendar button
 * - Color-coded borders (green=set, orange=expiring soon, red=expired)
 */
const DLCInput = forwardRef<HTMLInputElement, DLCInputProps>(function DLCInput({ value, onChange, compact = false, className = '', colorCoded = true }, ref) {
  const [textValue, setTextValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textInputRef = useRef<HTMLInputElement>(null);

  // Expose focus() via forwarded ref
  useImperativeHandle(ref, () => textInputRef.current as HTMLInputElement);
  const hiddenDateRef = useRef<HTMLInputElement>(null);

  // Sync text display from ISO value
  useEffect(() => {
    if (!isFocused) {
      if (value) {
        const parts = value.split('-');
        if (parts.length === 3) {
          setTextValue(`${parts[2]}/${parts[1]}/${parts[0]}`);
        }
      } else {
        setTextValue('');
      }
    }
  }, [value, isFocused]);

  const parseAndEmit = useCallback((input: string) => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');

    if (digits.length === 8) {
      const day = parseInt(digits.substring(0, 2), 10);
      const month = parseInt(digits.substring(2, 4), 10);
      const year = parseInt(digits.substring(4, 8), 10);

      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 2024 && year <= 2035) {
        const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        onChange(iso);
        return true;
      }
    }
    return false;
  }, [onChange]);

  // Auto-format: insert slashes as user types
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;

    // If user is deleting, allow it
    if (raw.length < textValue.length) {
      setTextValue(raw);
      // If cleared completely, emit empty
      if (raw === '') {
        onChange('');
      }
      return;
    }

    // Extract only digits from input
    const digits = raw.replace(/\D/g, '');

    // Auto-format with slashes
    let formatted = '';
    for (let i = 0; i < digits.length && i < 8; i++) {
      if (i === 2 || i === 4) formatted += '/';
      formatted += digits[i];
    }

    setTextValue(formatted);

    // Try to parse complete date
    if (digits.length === 8) {
      parseAndEmit(formatted);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      parseAndEmit(textValue);
      textInputRef.current?.blur();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (textValue && !parseAndEmit(textValue)) {
      // Restore the display from value if parse failed
      if (value) {
        const parts = value.split('-');
        if (parts.length === 3) {
          setTextValue(`${parts[2]}/${parts[1]}/${parts[0]}`);
        }
      } else {
        setTextValue('');
      }
    }
  };

  const openNativePicker = () => {
    hiddenDateRef.current?.showPicker?.();
    hiddenDateRef.current?.focus();
    hiddenDateRef.current?.click();
  };

  const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    if (newVal) {
      onChange(newVal);
    }
  };

  const handleClear = () => {
    onChange('');
    setTextValue('');
  };

  // Color coding
  let borderColor = 'border-gray-300';
  let bgColor = '';

  if (colorCoded && value) {
    const expiryDate = new Date(value + 'T00:00:00');
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const sevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    if (expiryDate < now) {
      borderColor = 'border-red-500';
      bgColor = 'bg-red-50';
    } else if (expiryDate <= sevenDays) {
      borderColor = 'border-orange-400';
      bgColor = 'bg-orange-50';
    } else {
      borderColor = 'border-green-400';
      bgColor = 'bg-green-50';
    }
  }

  const sizeClasses = compact
    ? 'min-h-[40px] text-sm px-2 py-1.5'
    : 'min-h-[48px] text-base px-3 py-2.5';

  return (
    <div className={`relative flex items-center ${className}`}>
      {/* Main text input for keyboard entry.
          `data-scan-target` : une date se tape en 8 chiffres d'affilée, ce qui a
          exactement la cadence d'une douchette. Sans ce marqueur, le hook de scan
          global prend la saisie pour un code-barres et lance une recherche
          produit — visible depuis que la douchette peut être activée sur iPad. */}
      <input
        ref={textInputRef}
        type="text"
        inputMode="numeric"
        data-scan-target
        value={textValue}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true);
          // Select all on focus for easy replacement
          setTimeout(() => textInputRef.current?.select(), 0);
        }}
        onBlur={handleBlur}
        placeholder="JJ/MM/AAAA"
        maxLength={10}
        className={`${sizeClasses} ${borderColor} ${bgColor} border-2 rounded-lg font-mono tracking-wider
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          w-full pr-16 transition-colors`}
      />

      {/* Calendar + Clear buttons */}
      <div className="absolute right-1 flex items-center gap-0.5">
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-gray-100 transition-colors"
            title="Effacer la DLC"
            tabIndex={-1}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={openNativePicker}
          className={`${compact ? 'p-1.5' : 'p-2'} text-gray-500 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors`}
          title="Ouvrir le calendrier"
          tabIndex={-1}
        >
          <svg className={compact ? 'w-4 h-4' : 'w-5 h-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>

      {/* Hidden native date input for calendar picker */}
      <input
        ref={hiddenDateRef}
        type="date"
        value={value}
        onChange={handleNativeDateChange}
        className="absolute opacity-0 w-0 h-0 pointer-events-none"
        tabIndex={-1}
      />
    </div>
  );
});

export default DLCInput;
